/**
 * BaselineRefreshModal.jsx — RC 1.47.2.BASELINE-GOVERNANCE (2026-05-02).
 *
 * Fires before Generate CV when the canonical Source baseline for the
 * current job is stale (the rubric/model version has bumped since the
 * baseline was created). Three actions:
 *
 *   1. Refresh My Baseline (Free)  — POSTs `/jobs/{id}/refresh-baseline`,
 *                                    then proceeds with the original
 *                                    Generate flow tagged
 *                                    `refresh_decision="refreshed"`.
 *   2. Continue Without Refresh    — proceeds with
 *                                    `refresh_decision="declined"`;
 *                                    backend treats baseline as
 *                                    nonexistent → no_baseline_protection
 *                                    (charge full).
 *   3. Cancel                      — closes the modal, no charge.
 *
 * All three actions are explicit user choices — the modal must be
 * shown every time the baseline is stale (operator's UX-A directive:
 * "trust-first, simple, no extra persistence").
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';

export function BaselineRefreshModal({
  open,
  onOpenChange,
  baselineVersion,
  currentVersion,
  onRefresh,
  onDecline,
  onCancel,
}) {
  const [busy, setBusy] = useState(null); // 'refresh' | 'decline' | null

  async function handleRefresh() {
    if (busy) return;
    setBusy('refresh');
    try { await onRefresh?.(); } finally { setBusy(null); }
  }

  async function handleDecline() {
    if (busy) return;
    setBusy('decline');
    try { await onDecline?.(); } finally { setBusy(null); }
  }

  function handleCancel() {
    if (busy) return;
    onCancel?.();
    onOpenChange?.(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange?.(v)}>
      <DialogContent
        data-testid="baseline-refresh-modal"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" aria-hidden />
            Updated scoring methodology
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm leading-relaxed text-muted-foreground">
            NDOR&apos;s scoring methodology has improved since your previous
            analysis. We can refresh your prior baseline at no charge to
            preserve the most accurate comparison.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          {(baselineVersion || currentVersion) && (
            <div
              data-testid="baseline-refresh-modal-version-chip"
              className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs"
            >
              <span className="font-medium text-muted-foreground">Versions:</span>
              <span>
                Baseline:&nbsp;
                <span className="font-mono">{baselineVersion || 'unstamped'}</span>
              </span>
              <span aria-hidden>·</span>
              <span>
                Current:&nbsp;
                <span className="font-mono">{currentVersion || '—'}</span>
              </span>
            </div>
          )}

          <ul className="space-y-1.5 text-sm">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-500">✓</span>
              <span>Refreshing is <strong>free</strong> — no Credits charged.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-500">✓</span>
              <span>
                Refresh preserves your charge protection (the ±5 confidence
                band still waives charges when the tailored CV doesn&apos;t
                materially improve on the refreshed baseline).
              </span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden />
              <span>
                Skipping leaves no baseline to compare against —
                tailoring will charge in full regardless of quality
                improvement.
              </span>
            </li>
          </ul>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            data-testid="baseline-refresh-modal-cancel-btn"
            variant="ghost"
            onClick={handleCancel}
            disabled={!!busy}
          >
            Cancel
          </Button>
          <Button
            data-testid="baseline-refresh-modal-decline-btn"
            variant="outline"
            onClick={handleDecline}
            disabled={!!busy}
          >
            {busy === 'decline' && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            )}
            Continue Without Refresh
          </Button>
          <Button
            data-testid="baseline-refresh-modal-refresh-btn"
            onClick={handleRefresh}
            disabled={!!busy}
          >
            {busy === 'refresh' && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            )}
            Refresh My Baseline (Free)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BaselineRefreshModal;
